// Backend errors arrive as { meta: { error: <string | string[] | { field: value }> }, data: {} }:
//   - string   → a business/logic error, already written for people ("Faktur ... tidak ditemukan.")
//   - string[] → request validation (ValidationPipe), one sentence per problem
//   - object   → a unique-constraint violation keyed by column ({ "outlets.name": "Cabang A" }),
//                or field errors ({ field: ["..."] })
// Some requests are rejected before the controller answers (a gateway 502, a proxy timeout) and
// carry no envelope at all, so the HTTP status has to speak for them. This resolves every shape
// to ONE readable Indonesian string and never returns anything but a string.

export const DEFAULT_ERROR_FALLBACK = "Terjadi kesalahan";

export const NETWORK_ERROR_MESSAGE =
  "Tidak dapat terhubung ke server. Periksa koneksi internet Anda lalu coba lagi.";
export const TIMEOUT_ERROR_MESSAGE =
  "Server terlalu lama menjawab. Coba lagi sebentar lagi.";
export const SESSION_EXPIRED_MESSAGE =
  "Sesi Anda telah berakhir. Silakan masuk kembali.";

const SERVER_ERROR_MESSAGE =
  "Server sedang bermasalah. Coba lagi sebentar lagi. Jika masih gagal, hubungi admin.";

const STATUS_MESSAGES = {
  400: "Data yang dikirim tidak valid. Periksa kembali isian Anda.",
  401: "Sesi Anda telah berakhir atau tidak valid. Silakan masuk kembali.",
  403: "Anda tidak memiliki izin untuk melakukan tindakan ini.",
  404: "Data yang diminta tidak ditemukan. Mungkin sudah dihapus.",
  409: "Data bertabrakan dengan data lain yang sudah ada.",
  413: "Ukuran data atau file terlalu besar.",
  422: "Data tidak dapat diproses. Periksa kembali isian Anda.",
  429: "Terlalu banyak permintaan. Tunggu sebentar lalu coba lagi.",
  500: SERVER_ERROR_MESSAGE,
  502: SERVER_ERROR_MESSAGE,
  503: SERVER_ERROR_MESSAGE,
  504: SERVER_ERROR_MESSAGE,
};

// Column keys the backend reports for a duplicated value, mapped to what a person calls it.
// Anything unlisted is humanized from the key itself.
const FIELD_LABELS = {
  name: "Nama",
  email: "Email",
  username: "Username",
  code: "Kode",
  phone: "Nomor telepon",
  sku: "SKU",
  referenceNumber: "Nomor referensi",
  sellerReferenceNumber: "Nomor referensi pemasok",
};

// The exact duplicate-column keys the backend reports, with the sentence staff have always seen
// for them. Keys arrive camel-cased by the axios case converter, so both "table.column" and
// "tableColumn" spellings occur.
const DUPLICATE_MESSAGES = {
  "customers.email": "Email sudah digunakan",
  "expenseCategories.name": "Nama kategori biaya sudah digunakan",
  "outlets.name": "Nama toko sudah digunakan",
  productVariantAttributesName: "Nama atribut varian produk sudah digunakan",
  "paymentTerms.name": "Nama syarat pembayaran sudah digunakan",
  paymentMethodsName: "Nama metode pembayaran sudah digunakan",
  "supplierCategories.name": "Nama kategori pemasok sudah digunakan",
  promotionCampaignsName: "Nama kampanye promosi sudah digunakan",
  "unitOfMeasurements.name": "Nama satuan ukuran sudah digunakan",
  unitOfMeasurementsName: "Nama satuan ukuran sudah digunakan",
  productCategoriesName: "Nama kategori produk sudah digunakan",
  "users.username": "Username sudah digunakan",
  "users.email": "Email sudah digunakan",
  "companies.name": "Nama perusahaan sudah digunakan",
  "suppliers.code": "Kode pemasok sudah digunakan",
  sellerReferenceNumber: "No faktur penjual sudah ada",
};

const endsLikeSentence = (text) => /[.!?]$/.test(text.trim());

const isNonEmptyString = (value) =>
  typeof value === "string" && value.trim() !== "";

const trimEndingPunctuation = (text) => text.trim().replace(/[.!?]+$/, "");

// Sentences already end in a full stop, so they read best separated by a space; bare fragments
// get a comma.
const joinMessages = (messages) => {
  const cleaned = messages.map((message) => message.trim());
  return cleaned.every(endsLikeSentence)
    ? cleaned.join(" ")
    : cleaned.join(", ");
};

const humanizeKey = (key) => {
  const last = key.split(".").pop();
  if (FIELD_LABELS[last]) return FIELD_LABELS[last];
  const words = last
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
};

// `{ "outlets.name": "Cabang A" }` → `Nama "Cabang A" sudah digunakan.`; an entry that is itself a
// sentence (the phone-number check answers with one) is passed through.
const describeFieldEntry = ([key, value]) => {
  if (DUPLICATE_MESSAGES[key]) return [`${DUPLICATE_MESSAGES[key]}.`];
  if (Array.isArray(value)) {
    return value.filter(isNonEmptyString).map((text) => text.trim());
  }
  if (!isNonEmptyString(value)) return [];
  if (endsLikeSentence(value)) return [value.trim()];
  return [`${humanizeKey(key)} "${value}" sudah digunakan.`];
};

const messagesFromMetaError = (metaError) => {
  if (isNonEmptyString(metaError)) return [metaError.trim()];

  if (Array.isArray(metaError)) {
    return metaError.flat().filter(isNonEmptyString);
  }

  if (metaError && typeof metaError === "object") {
    return Object.entries(metaError).flatMap(describeFieldEntry);
  }

  return [];
};

// The failure gives a reason, the caller's fallback says what was being attempted:
// "Gagal menyimpan pelanggan. Server sedang bermasalah ..."
const withContext = (fallback, reason) =>
  fallback && fallback !== DEFAULT_ERROR_FALLBACK
    ? `${trimEndingPunctuation(fallback)}. ${reason}`
    : reason;

export const getErrorMessage = (error, fallback = DEFAULT_ERROR_FALLBACK) => {
  if (error?.sessionExpired) return SESSION_EXPIRED_MESSAGE;

  const response = error?.response;
  const data = response?.data;

  const metaMessages = messagesFromMetaError(data?.meta?.error);
  if (metaMessages.length) return joinMessages(metaMessages);

  // Requests rejected before the controller ran (NestJS ValidationPipe, guards)
  // that skip the envelope send { message: string | string[] } instead.
  const bodyMessages = messagesFromMetaError(data?.message);
  if (bodyMessages.length) return joinMessages(bodyMessages);

  if (response) {
    const statusMessage = STATUS_MESSAGES[response.status];
    return statusMessage ? withContext(fallback, statusMessage) : fallback;
  }

  // No response at all: the request never got an answer.
  if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") {
    return withContext(fallback, TIMEOUT_ERROR_MESSAGE);
  }
  if (error?.code === "ERR_NETWORK" || error?.message === "Network Error") {
    return withContext(fallback, NETWORK_ERROR_MESSAGE);
  }

  // A plain Error thrown by our own code carries a message written for the user
  // (e.g. "Kuantitas harus lebih dari 0"). axios' own messages never reach here.
  if (isNonEmptyString(error?.message) && !error?.isAxiosError) {
    return error.message;
  }

  return fallback;
};
